import { reportFailure } from "./telemetry";
import { operationFor } from "../shared/telemetry";
import {
  diagnosticRoute,
  redact,
  type BugReport,
  type DiagnosticEvent,
} from "../shared/bug-reports";
const errors: DiagnosticEvent[] = [],
  network: DiagnosticEvent[] = [];
const age = 10 * 60_000;
export function recordDiagnostic(
  kind: string,
  message: string,
  isNetwork = false,
) {
  try {
    const list = isNetwork ? network : errors;
    list.push({
      at: Date.now(),
      kind,
      message: redact(message).slice(0, 1000),
    });
    while (
      list.length > (isNetwork ? 30 : 50) ||
      (list[0] && list[0].at < Date.now() - age)
    )
      list.shift();
  } catch {
    /* Diagnostics must never break the editor. */
  }
}
export function clearDiagnostics() {
  errors.length = 0;
  network.length = 0;
}
function describe(value: unknown): string {
  try {
    if (value instanceof Error)
      return `${String(value.name).slice(0, 100)}: ${String(value.message).slice(0, 2000)}\n${String(value.stack ?? "").slice(0, 6000)}`;
    if (typeof value === "string") return value.slice(0, 6000);
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
  } catch {
    return "[unreadable error]";
  }
  return "[object omitted]"; // Never serialize arbitrary user records.
}

export function captureDiagnostics(): NonNullable<BugReport["diagnostics"]> {
  const events = [...errors, ...network]
    .filter((e) => e.at >= Date.now() - age)
    .sort((a, b) => a.at - b.at)
    .map((e) => ({ ...e }));
  while (new TextEncoder().encode(JSON.stringify(events)).length > 60_000)
    events.shift();
  return {
    build:
      typeof __PLANROOM_BUILD__ === "string"
        ? __PLANROOM_BUILD__
        : "development",
    browser: navigator.userAgent.slice(0, 300),
    viewport: `${innerWidth} × ${innerHeight} @ ${devicePixelRatio}`,
    online: navigator.onLine,
    page: new URLSearchParams(location.search).has("room")
      ? "project board"
      : "projects or sign-in",
    events,
  };
}
let installed = false;
export function installDiagnostics() {
  if (installed) return;
  installed = true;
  for (const level of ["warn", "error"] as const) {
    const original = console[level];
    console[level] = (...args: unknown[]) => {
      recordDiagnostic(
        `console.${level}`,
        args.slice(0, 5).map(describe).join(" "),
      );
      Reflect.apply(original, console, args);
    };
  }
  window.addEventListener(
    "error",
    (event: Event) => {
      if (event instanceof ErrorEvent) {
        recordDiagnostic("error", describe(event.error ?? event.message));
        reportFailure("runtime", "other", event.error);
      } else if (
        event.target instanceof HTMLImageElement ||
        event.target instanceof HTMLScriptElement
      )
        recordDiagnostic(
          "resource",
          diagnosticRoute(event.target.src, location.origin),
        );
    },
    true,
  );
  window.addEventListener("unhandledrejection", (event) => {
    recordDiagnostic("rejection", describe(event.reason));
    reportFailure("rejection", "other", event.reason);
  });
  window.addEventListener("online", () =>
    recordDiagnostic("connection", "Browser online"),
  );
  window.addEventListener("offline", () =>
    recordDiagnostic("connection", "Browser offline"),
  );
  window.addEventListener("planroom:notice", (event) =>
    recordDiagnostic("notice", describe((event as CustomEvent).detail)),
  );
  const originalFetch = window.fetch;
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;
    const raw = input instanceof Request ? input.url : String(input);
    const route = diagnosticRoute(raw, location.origin);
    const method = (
      init?.method ?? (input instanceof Request ? input.method : "GET")
    )
      .toUpperCase()
      .replace(/[^A-Z]/g, "")
      .slice(0, 12);
    const start = performance.now();
    const operation = operationFor(raw, location.origin);
    const skip = route === "/api/bug-reports" || operation === "telemetry";
    try {
      const response = await Reflect.apply(originalFetch, window, args);
      if (!skip) {
        const ref = response.headers.get("X-Request-Id");
        if (response.status >= 500 && operation !== "other")
          reportFailure("request", operation, undefined, {
            status: response.status,
            durationMs: Math.round(performance.now() - start),
            requestId: ref ?? "",
          });
        recordDiagnostic(
          "fetch",
          `${method} ${route} → ${response.status} (${Math.round(performance.now() - start)} ms)${ref && /^[a-f0-9-]{36}$/.test(ref) ? ` request=${ref}` : ""}`,
          response.ok,
        );
      }
      if (route === "/api/logout" && response.ok) clearDiagnostics();
      return response;
    } catch (error) {
      if (
        !skip &&
        operation !== "other" &&
        !(error instanceof Error && error.name === "AbortError")
      )
        reportFailure("request", operation, error, {
          durationMs: Math.round(performance.now() - start),
        });
      if (!skip)
        recordDiagnostic(
          "fetch",
          `${method} ${route} → failed (${Math.round(performance.now() - start)} ms): ${describe(error)}`,
        );
      throw error;
    }
  };
  const NativeSocket = window.WebSocket;
  window.WebSocket = new Proxy(NativeSocket, {
    construct(target, args) {
      const socket = Reflect.construct(target, args) as WebSocket;
      const route = diagnosticRoute(String(args[0]), location.origin);
      socket.addEventListener("open", () =>
        recordDiagnostic("websocket", `${route} open`, true),
      );
      socket.addEventListener("error", () =>
        recordDiagnostic("websocket", `${route} connection error`),
      );
      socket.addEventListener("close", (e) => {
        // Normal close, page leave and policy close (access revoked) are expected.
        if (
          e.code !== 1000 &&
          e.code !== 1001 &&
          e.code !== 1008 &&
          operationFor(
            String(args[0]).replace(/^ws/, "http"),
            location.origin,
          ) === "sync"
        )
          reportFailure("socket", "sync", undefined, { status: e.code });
        recordDiagnostic(
          "websocket",
          `${route} closed (${e.code}, ${e.wasClean ? "clean" : "unclean"})`,
          e.wasClean,
        );
      });
      return socket;
    },
  });
}
